import { useEffect, useState } from "react";
import { Head, Link } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Heading,
  Link as RadixLink,
  Spinner,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";

import { AppLayout } from "@/components/app-layout";
import { createApiToken, fetchApiTokens, revokeApiToken, type ApiTokenListItem } from "@/lib/api";
import type { SharedProps } from "@/lib/types";

type ApiKeysIndexProps = SharedProps;

export default function ApiKeysIndex({ auth }: ApiKeysIndexProps) {
  const [tokens, setTokens] = useState<ApiTokenListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [newTokenValue, setNewTokenValue] = useState<string | null>(null);

  useEffect(() => {
    void loadTokens();
  }, []);

  async function loadTokens() {
    setLoading(true);
    setError(null);
    try {
      setTokens(await fetchApiTokens());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load API tokens");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateToken() {
    if (!name.trim()) {
      setError("Token name is required.");
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const result = await createApiToken({
        name: name.trim(),
        expires_at: expiresAt || null,
      });
      setNewTokenValue(result.token.plaintext_token);
      setCreateOpen(false);
      setName("");
      setExpiresAt("");
      await loadTokens();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create token");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevokeToken(id: number) {
    const confirmed = window.confirm("Revoke this token? It will stop working immediately.");
    if (!confirmed) return;

    setError(null);
    try {
      await revokeApiToken(id);
      await loadTokens();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke token");
    }
  }

  async function copyNewToken() {
    if (!newTokenValue) return;
    await navigator.clipboard.writeText(newTokenValue);
  }

  return (
    <>
      <Head title="API Tokens" />
      <AppLayout
        user={auth.user}
        activeModule="weekly_planner"
        onModuleChange={() => {}}
        pageTitle="API Tokens"
        pageActions={
          <Flex gap="2">
            <Link href="/">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
            <Dialog.Root open={createOpen} onOpenChange={setCreateOpen}>
              <Dialog.Trigger>
                <Button>Create Token</Button>
              </Dialog.Trigger>
              <Dialog.Content maxWidth="520px">
                <Dialog.Title>Create API Token</Dialog.Title>
                <Dialog.Description size="2">
                  Token values are shown once and cannot be recovered later.
                </Dialog.Description>
                <Flex direction="column" gap="3" mt="3">
                  <Box>
                    <Text as="label" size="2" weight="medium">
                      Token Name
                    </Text>
                    <TextField.Root
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="e.g. local-cli"
                    />
                  </Box>
                  <Box>
                    <Text as="label" size="2" weight="medium">
                      Expires On (optional)
                    </Text>
                    <TextField.Root
                      type="date"
                      value={expiresAt}
                      onChange={(event) => setExpiresAt(event.target.value)}
                    />
                  </Box>
                </Flex>
                <Flex justify="end" gap="2" mt="4">
                  <Dialog.Close>
                    <Button variant="soft" color="gray">
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button onClick={() => void handleCreateToken()} disabled={creating}>
                    {creating ? "Creating..." : "Create Token"}
                  </Button>
                </Flex>
              </Dialog.Content>
            </Dialog.Root>
          </Flex>
        }
      >
        <Flex direction="column" gap="3">
          <Card className="p-4">
            <Flex justify="between" align="center" gap="3" wrap="wrap">
              <Box>
                <Heading size="4">Personal Access Tokens</Heading>
                <Text size="2" color="gray">
                  Use tokens with <code>Authorization: Bearer &lt;token&gt;</code>.
                </Text>
              </Box>
              <RadixLink href="/api/docs" target="_blank">
                View API Documentation
              </RadixLink>
            </Flex>
          </Card>

          {error ? (
            <Card className="p-3">
              <Text color="red">{error}</Text>
            </Card>
          ) : null}

          {newTokenValue ? (
            <Card className="p-4">
              <Flex direction="column" gap="2">
                <Text weight="bold">Copy your new token now</Text>
                <Text size="2" color="gray">
                  This value will not be shown again.
                </Text>
                <TextField.Root value={newTokenValue} readOnly />
                <Flex gap="2">
                  <Button variant="soft" onClick={() => void copyNewToken()}>
                    Copy Token
                  </Button>
                  <Button variant="outline" onClick={() => setNewTokenValue(null)}>
                    Dismiss
                  </Button>
                </Flex>
              </Flex>
            </Card>
          ) : null}

          <Card className="p-0 overflow-hidden">
            {loading ? (
              <Flex p="4" align="center" gap="2">
                <Spinner size="1" />
                <Text size="2">Loading tokens...</Text>
              </Flex>
            ) : (
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Last Used</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Expires</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {tokens.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={6}>
                        <Text size="2" color="gray">
                          No tokens yet.
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    tokens.map((token) => (
                      <Table.Row key={token.id}>
                        <Table.Cell>{token.name}</Table.Cell>
                        <Table.Cell>
                          {token.active ? (
                            <Badge color="green">Active</Badge>
                          ) : (
                            <Badge color="gray">Inactive</Badge>
                          )}
                        </Table.Cell>
                        <Table.Cell>{formatDate(token.created_at)}</Table.Cell>
                        <Table.Cell>
                          {token.last_used_at ? formatDate(token.last_used_at) : "Never"}
                        </Table.Cell>
                        <Table.Cell>
                          {token.expires_at ? formatDate(token.expires_at) : "No expiry"}
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            variant="soft"
                            color="red"
                            size="1"
                            disabled={!token.active}
                            onClick={() => void handleRevokeToken(token.id)}
                          >
                            Revoke
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            )}
          </Card>
        </Flex>
      </AppLayout>
    </>
  );
}

function formatDate(value: string) {
  const parsed = new Date(value);
  return parsed.toLocaleString();
}
